import { FC, FormEvent, useEffect, useId, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import { privacyPolicyUrl } from 'src/utils/siteLinks';
import {
  FREE_MONTH_MAX,
  FreeMonthLocale,
  buildFreeMonthHandoffUrl,
  looksLikeEmail,
} from 'src/utils/freeMonth';

type Props = {
  open: boolean;
  onClose: () => void;
  /** Validated PROD /start URL (see freeMonthStartUrl). */
  startUrl: string;
  locale: FreeMonthLocale;
};

/**
 * Free-month pop-up (FM-16). The three fields live in component state only
 * and are dropped on close. Nothing is sent, stored or logged here: "Continue"
 * navigates this tab to PROD /start with the fields in the URL fragment.
 */
export const DemoFreeMonthDialog: FC<Props> = ({ open, onClose, startUrl, locale }) => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const baseId = useId();
  const titleId = `${baseId}-title`;
  const bodyId = `${baseId}-body`;
  const errorId = `${baseId}-error`;
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [organisation, setOrganisation] = useState('');
  const [invalid, setInvalid] = useState(false);

  useEffect(() => {
    if (open) return;
    setName('');
    setEmail('');
    setOrganisation('');
    setInvalid(false);
  }, [open]);

  const complete = !!name.trim() && !!email.trim() && !!organisation.trim();

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (!complete) return;
    if (!looksLikeEmail(email)) {
      setInvalid(true);
      return;
    }
    window.location.assign(
      buildFreeMonthHandoffUrl(startUrl, { name, email, organisation }, locale)
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      fullScreen={fullScreen}
      aria-labelledby={titleId}
      aria-describedby={bodyId}
    >
      <DialogTitle id={titleId} sx={{ pr: 7 }}>
        {t('demo.freeMonth.title')}
        <IconButton
          aria-label={t('close') as string}
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <Box component="form" onSubmit={onSubmit} noValidate data-testid="free-month-form">
        <DialogContent>
          <Typography id={bodyId} variant="body2" sx={{ mb: 2 }}>
            {t('demo.freeMonth.body')}
          </Typography>
          <TextField
            label={t('demo.freeMonth.nameLabel')}
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoComplete="name"
            required
            fullWidth
            margin="dense"
            inputProps={{ maxLength: FREE_MONTH_MAX.name, 'data-testid': 'free-month-name' }}
          />
          <TextField
            label={t('demo.freeMonth.emailLabel')}
            type="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setInvalid(false);
            }}
            autoComplete="email"
            required
            fullWidth
            margin="dense"
            error={invalid}
            inputProps={{
              maxLength: FREE_MONTH_MAX.email,
              'data-testid': 'free-month-email',
              'aria-describedby': invalid ? errorId : undefined,
            }}
          />
          <TextField
            label={t('demo.freeMonth.orgLabel')}
            value={organisation}
            onChange={(e) => setOrganisation(e.target.value)}
            autoComplete="organization"
            required
            fullWidth
            margin="dense"
            inputProps={{
              maxLength: FREE_MONTH_MAX.organisation,
              'data-testid': 'free-month-org',
            }}
          />
          {invalid && (
            <Typography
              id={errorId}
              role="alert"
              variant="body2"
              color="error"
              data-testid="free-month-error"
              sx={{ mt: 1 }}
            >
              {t('demo.lead.errorInvalid')}
            </Typography>
          )}
          <Typography
            variant="caption"
            color="text.secondary"
            component="p"
            data-testid="free-month-consent"
            sx={{ mt: 2 }}
          >
            {t('demo.freeMonth.consent')}
          </Typography>
          <Link
            href={privacyPolicyUrl(i18n.language)}
            target="_blank"
            rel="noopener noreferrer"
            variant="caption"
          >
            {t('demo.freeMonth.privacyLink')}
          </Link>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={onClose} data-testid="free-month-close">
            {t('close')}
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={!complete}
            data-testid="free-month-continue"
            sx={{ minHeight: 44 }}
          >
            {t('demo.freeMonth.continue')}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};
